<script setup lang="ts">
import type { Schemas } from '#shopware'

interface EventCalenderSlide {
    date?: string
    image?: Schemas['EventCalenderSlide']['image']
    logo?: Schemas['EventCalenderSlide']['logo']
    number?: string
    text?: string
    textColorWhite: boolean
    linkTarget?: string
}

const props = defineProps<{
    slide: EventCalenderSlide
    openAutomatically: boolean
}>()

const isFlipped = ref(false)

const hasDate = computed(() => !!props.slide?.date)

const slideDate = computed(() => {
    if (!hasDate.value) return null
    return new Date(props.slide.date!)
})

const today = computed(() => {
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    return now
})

const isPast = computed(() => {
    if (!slideDate.value) return false
    const date = new Date(slideDate.value)
    date.setHours(0, 0, 0, 0)
    return date < today.value
})

const isToday = computed(() => {
    if (!slideDate.value) return false
    const date = new Date(slideDate.value)
    date.setHours(0, 0, 0, 0)
    return date.getTime() === today.value.getTime()
})

const isExpired = computed(() => isPast.value)

const isClickable = computed(() =>
    isToday.value && !props.openAutomatically && !isFlipped.value
)

watch(isExpired, (expired) => {
    if (expired) isFlipped.value = true
}, { immediate: true })

watch([isToday, () => props.openAutomatically], ([todayVal, openAuto]) => {
    if (todayVal && openAuto) isFlipped.value = true
}, { immediate: true })

function handleClick() {
    if (isClickable.value) isFlipped.value = !isFlipped.value
}
</script>

<template>
    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
    <div
        class="relative transition-all duration-300 mx-auto sm:mx-0 w-[348px] sm:w-full h-[488px] xl:h-[540px] 2xl:h-[502px] shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-outline-focus focus-visible:ring-3 focus-visible:ring-neutral-content"
        :class="{ 'cursor-pointer': isClickable }"
        tabindex="0"
        :aria-label="isFlipped ? $t('eventCalender.ariaLabelFlipped') : (isClickable ? $t('eventCalender.ariaLabelClickable') : $t('eventCalender.ariaLabelNotClickable'))"
        @click="!isFlipped ? handleClick() : undefined"
        @keydown.enter="!isFlipped ? handleClick() : undefined"
    >
        <!-- Back side (default view) -->
        <div
            v-if="!isFlipped"
            class="relative w-full h-full flex flex-col"
        >
            <MiscImage
                v-if="slide.logo"
                :src="slide.logo.path"
                :alt="slide.logo.translated?.alt"
                width="1421"
                height="1991"
                sizes="343px md:348px lg:348px xl:386px 2xl:411px"
                class="h-full w-full object-cover"
                :lazy-load="true"
                :preload="false"
            />
            <div
                v-if="slide.number"
                class="absolute inset-x-0 bottom-[37px] md:bottom-[40px] lg:bottom-[37px] xl:bottom-[52px] 2xl:bottom-[42px] flex items-center justify-center text-3xl font-fjalla-one"
                :class="[slide.textColorWhite ? 'text-white' : 'text-black']"
            >
                {{ slide.number }}
            </div>
        </div>

        <!-- Front side (flipped view) -->
        <template v-else>
            <template v-if="slide.linkTarget">
                <MiscLink
                    :to="slide.linkTarget"
                    class="relative w-full block"
                    :class="[slide.text ? 'h-[388px] md:h-[388px] lg:h-[388px] xl:h-[436px] 2xl:h-[400px]' : 'h-full']"
                >
                    <MiscImage
                        v-if="slide.image"
                        :src="slide.image.path"
                        :alt="slide.image.translated?.alt"
                        width="1421"
                        height="1991"
                        sizes="347px md:348px lg:348px xl:386px 2xl:411px"
                        class="h-full w-full object-cover aspect-1/1"
                        :lazy-load="true"
                        :preload="false"
                    />
                    <div
                        v-if="slide.text"
                        class="px-4 py-9 text-xl text-black text-center leading-[1.875rem] xl:leading-[1.5]"
                    >
                        {{ slide.text }}
                    </div>
                </MiscLink>
            </template>
            <template v-else>
                <div
                    class="relative w-full"
                    :class="[slide.text ? 'h-[388px] md:h-[388px] lg:h-[388px] xl:h-[436px] 2xl:h-[400px]' : 'h-full']"
                >
                    <MiscImage
                        v-if="slide.image"
                        :src="slide.image.path"
                        :alt="slide.image.translated?.alt"
                        width="1421"
                        height="1991"
                        sizes="347px md:348px lg:348px xl:386px 2xl:411px"
                        class="h-full w-full object-cover aspect-1/1"
                        :lazy-load="true"
                        :preload="false"
                    />
                    <div
                        v-if="slide.text"
                        class="px-4 py-9 text-xl text-black text-center"
                    >
                        {{ slide.text }}
                    </div>
                </div>
            </template>
        </template>

        <!-- Expired overlay -->
        <div
            v-if="isExpired"
            class="absolute inset-0 flex items-center justify-center bg-black/50"
        >
            <span class="text-6.5xl font-fjalla-one text-white uppercase">
                {{ $t('eventCalender.expired') }}
            </span>
        </div>
    </div>
</template>